import { formatVertical } from '@moda/portal-stanchions';
import { useMemo } from 'react';
import { usePLPParams } from './usePLPParams';

type MetaData = {
  header: string;
  title: string;
  description: string;
};

const STATIC_METADATA: Record<string, MetaData> = {
  'shop/home': {
    header: 'Home Décor & Accessories',
    title: 'Luxury Home Décor & Accessories',
    description:
      'Shop the latest luxury and designer home decor including accessories, dinnerware and bedding, all at Moda Operandi.'
  },
  'shop/beauty': {
    header: 'All Beauty',
    title: 'All Beauty',
    description: 'Shop the latest beauty products at Moda Operandi.'
  },
  'shop/home/children': {
    header: 'Kids',
    title: 'Kids',
    description: 'Shop the latest kids collection at Moda Operandi.'
  },
  'sale/home/children': {
    header: 'Kids Sale',
    title: 'Kids Sale',
    description: 'Shop the latest kids collection sale at Moda Operandi.'
  },
  'new/home/children': {
    header: 'New Kids',
    title: 'New Kids',
    description: 'Shop new kids collection at Moda Operandi.'
  },
  'shop/home/home-fragrance': {
    header: 'Home Fragrance',
    title: 'Home Fragrance',
    description: 'Shop the latest home fragrance collection at Moda Operandi.'
  },
  'sale/home/home-fragrance': {
    header: 'Home Fragrance Sale',
    title: 'Home Fragrance Sale',
    description: 'Shop the latest home fragrance sale at Moda Operandi.'
  },
  'new/home/home-fragrance': {
    header: 'New Home Fragrance',
    title: 'New Home Fragrance',
    description: 'Shop new home fragrance at Moda Operandi.'
  },
  'shop/home/luggage-and-travel-accessories': {
    header: 'Luggage & Travel',
    title: 'Luggage & Travel',
    description: 'Shop the latest luggage and travel accessories at Moda Operandi.'
  },
  'sale/home/luggage-and-travel-accessories': {
    header: 'Luggage & Travel Sale',
    title: 'Luggage & Travel Sale',
    description: 'Shop the latest luggage and travel accessories sale at Moda Operandi.'
  },
  'new/home/luggage-and-travel-accessories': {
    header: 'New Luggage & Travel',
    title: 'New Luggage & Travel',
    description: 'Shop new luggage and travel accessories at Moda Operandi.'
  },
  'shop/home/bedding': {
    header: 'Bedding',
    title: 'Bedding',
    description: 'Shop the latest bedding collection at Moda Operandi.'
  },
  'sale/home/bedding': {
    header: 'Bedding Sale',
    title: 'Bedding Sale',
    description: 'Shop the latest bedding sale at Moda Operandi.'
  },
  'new/home/bedding': {
    header: 'New Bedding',
    title: 'New Bedding',
    description: 'Shop new bedding collection at Moda Operandi.'
  },
  'shop/home/table-linens': {
    header: 'Table Linens',
    title: 'Table Linens',
    description: 'Shop the latest table linens collection at Moda Operandi.'
  },
  'sale/home/table-linens': {
    header: 'Table Linens Sale',
    title: 'Table Linens Sale',
    description: 'Shop the latest table linens sale at Moda Operandi.'
  },
  'new/home/table-linens': {
    header: 'New Table Linens',
    title: 'New Table Linens',
    description: 'Shop new table linens collection at Moda Operandi.'
  },
  'shop/home/dining-and-entertaining': {
    header: 'Dining',
    title: 'Dining',
    description: 'Shop the latest dining collection at Moda Operandi.'
  },
  'sale/home/dining-and-entertaining': {
    header: 'Dining Sale',
    title: 'Dining Sale',
    description: 'Shop the latest dining sale collection at Moda Operandi.'
  },
  'new/home/dining-and-entertaining': {
    header: 'New Dining',
    title: 'New Dining',
    description: 'Shop new dining collection at Moda Operandi.'
  },
  'shop/home/home-decor': {
    header: 'Home Decor',
    title: 'Home Decor',
    description: 'Shop the latest home decor collection at Moda Operandi.'
  },
  'sale/home/home-decor': {
    header: 'Home Decor Sale',
    title: 'Home Decor Sale',
    description: 'Shop the latest home decor sale collection at Moda Operandi.'
  },
  'new/home/home-decor': {
    header: 'New Home Decor',
    title: 'New Home Decor',
    description: 'Shop new home decor collection at Moda Operandi.'
  },
  'sale/home': {
    header: 'Sale',
    title: 'Sale',
    description: 'Shop the latest home sale collection at Moda Operandi.'
  }
};

const MAX_DESIGNER_LIST = 5;

export const useCategoryPLPMeta = ({ designers: availableDesigners }: { designers?: string[] }) => {
  const {
    params: { section = 'shop', vertical, subcategory, category },
    queryVariables
  } = usePLPParams();

  const designers = useMemo(() => {
    if (!availableDesigners) return null;
    if (availableDesigners.length === 1) return availableDesigners[0];
    if (availableDesigners.length <= MAX_DESIGNER_LIST) {
      return `${availableDesigners.slice(0, -1).join(', ')} and ${availableDesigners.slice(-1)}`;
    }
    return `${availableDesigners
      .slice(0, MAX_DESIGNER_LIST - 1)
      .join(', ')} and ${availableDesigners.slice(MAX_DESIGNER_LIST - 1, MAX_DESIGNER_LIST)}`;
  }, [availableDesigners]);

  const verticalText = formatVertical(vertical);
  const firstAttribute = queryVariables.attributes?.[0];
  const categoryText = firstAttribute || subcategory || category;

  const categoryPath = [section, vertical, category, subcategory, firstAttribute]
    .filter(Boolean)
    .join('/');

  const staticMetadata = STATIC_METADATA[categoryPath];

  if (staticMetadata) return staticMetadata;

  if (section === 'sale') {
    return {
      header:
        vertical === 'beauty'
          ? `${categoryText || verticalText} Sale`
          : `${verticalText} Designer ${categoryText || 'Collection'} Sale`,
      title:
        vertical === 'beauty'
          ? `${categoryText || verticalText} Sale`
          : `${verticalText} Designer ${categoryText || 'Collection'} Sale`,
      description: designers
        ? `Shop ${verticalText} ${
            categoryText ? categoryText + ' ' : ''
          }sale now at Moda Operandi, featuring luxury fashion designers such as ${designers}.`
        : `Shop the world's best ${verticalText} designer ${
            categoryText ? categoryText + ' ' : ''
          }sale now at Moda Operandi. Discover the latest fashion trends, most coveted pieces, and more.`
    };
  }

  if (section === 'new') {
    if (vertical === 'home' && !categoryText) {
      return {
        header: 'New Home',
        title: 'New Home',
        description: designers
          ? `Shop new home at Moda Operandi, featuring luxury designers such as ${designers}.`
          : `Shop new home at Moda Operandi. Discover the latest fashion trends, most coveted pieces, and more.`
      };
    }
    return {
      header:
        vertical === 'beauty'
          ? `New ${categoryText || verticalText}`
          : `New ${verticalText} Designer ${categoryText || 'Clothing & Fashion'}`,
      title:
        vertical === 'beauty'
          ? `New ${categoryText || verticalText}`
          : `New ${verticalText} Designer ${categoryText || 'Clothing & Fashion'}`,
      description: designers
        ? `Shop new ${verticalText} ${
            categoryText || 'clothing & fashion'
          } at Moda Operandi, featuring luxury fashion designers such as ${designers}.`
        : `Shop new ${verticalText} designer ${
            categoryText || 'clothing & fashion'
          } at Moda Operandi. Browse our boutique of expertly curated selection featuring the latest fashion trends.`
    };
  }

  return {
    header:
      vertical === 'beauty'
        ? categoryText || verticalText
        : `${verticalText} Designer ${categoryText}`,
    title: firstAttribute
      ? vertical === 'beauty'
        ? firstAttribute
        : `${verticalText} Designer ${firstAttribute}`
      : subcategory
        ? vertical === 'beauty'
          ? subcategory
          : `${verticalText} Designer ${subcategory}`
        : category
          ? vertical === 'beauty'
            ? category
            : `${verticalText} Designer ${category} & Luxury Fashion`
          : `${verticalText} Designer Collections & Luxury Fashion`,
    description: designers
      ? `Shop the latest ${verticalText} ${categoryText} collection at Moda Operandi, featuring luxury fashion designers such as ${designers}.`
      : `Shop the latest ${verticalText} designer ${categoryText} collection at Moda Operandi.`
  };
};
